/**
 * ehr#709 - why a User Roles control is locked, in words. Built-in roles and the Admin
 * role's rights are fixed by design; every locked control says so instead of sitting grey.
 */
import { roleInUseMessage } from '@ehr/shared';

/** Shown when nobody has added a role of their own yet. */
export const NO_CUSTOM_ROLES_HINT =
  'Built-in roles are fixed. Click + Add Role to make a role of your own: you can rename it, set its rights, and delete it.';

/** Why a built-in role's rename / delete control does nothing. */
export const builtinLockNote = (label: string, action: 'rename' | 'delete') =>
  action === 'rename'
    ? `“${label}” is a built-in role, so its name is fixed. Click + Add Role to make a role you can name yourself.`
    : `“${label}” is a built-in role, so it can’t be deleted. Roles you add with + Add Role can be deleted.`;

/** Tooltip for a built-in role's locked rename / delete control. */
export const builtinLockTitle = (action: 'rename' | 'delete') =>
  action === 'rename' ? 'Built-in role names are fixed' : 'Built-in roles can’t be deleted';

/**
 * A custom role still held by active users can't be deleted (the server refuses too,
 * 409 ROLE_IN_USE). Returns the message to show, or null when it can go.
 */
export function roleDeleteBlock(
  role: { id: string; name: string },
  users: { customRoleId: string | null; active: boolean }[],
): string | null {
  const n = users.filter((u) => u.active && u.customRoleId === role.id).length;
  return n ? roleInUseMessage(role.name, n) : null;
}

/** Tooltip for a role's Rights button. */
export function rightsButtonTitle(roleKey: string, saved: boolean, set: boolean): string {
  if (!saved) return 'Save the new role first, then set its rights';
  if (roleKey === 'sys:admin') return 'The Admin role always has every right';
  return set ? 'Edit which features this role can use' : 'Set which features this role can use';
}

/** Tooltip for the roles list Save button. */
export const saveButtonTitle = (dirty: boolean, saving: boolean) =>
  saving ? 'Saving…' : dirty ? 'Save your role changes' : 'No changes to save yet';
